import {
  ChangeEvent,
  FocusEvent,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useMemo,
  useCallback,
} from 'react';
import classNames from 'classnames';
import { DropdownList } from '@components/dropdown';
import { Icon } from '@components/icon';
import { AssistiveText } from '@components/input/AssistiveText';
import { Input } from '@components/input/Input';
import './SelectDropdown.scss';

type OnChangeFunctionType = (e: ChangeEvent<HTMLSelectElement>) => void;
type OnFocusFunctionType = (e: FocusEvent<HTMLDivElement>) => void;
type OnBlurFunctionType = (e: FocusEvent<HTMLDivElement>) => void;

export interface SelectItemOptions {
  label: string;
  value: string;
  selected?: boolean;
}

export interface SelectProps {
  name?: string;
  disabled?: boolean;
  onChange?: OnChangeFunctionType;
  onFocus?: OnFocusFunctionType;
  onBlur?: OnBlurFunctionType;
  options?: Array<SelectItemOptions>;
  assistiveText?: string;
  id: string;
  required?: boolean;
  label: string;
  error?: boolean;
  defaultValue?: string;
  value?: HTMLSelectElement['value'];
  size?: 'large' | 'small';
  searchable?: boolean;
  className?: string;
}

/**
 * Select component
 *
 * @param name - Name for the select field.
 * @param disabled - If true, select is disabled.
 * @param onChange - Change callback event.
 * @param onFocus - Focus callback event.
 * @param onBlur - Blur callback event.
 * @param options - Array of values the select type for the component. Default "text".
 * @param assistiveText - Assistive text for the select field.
 * @param id - Id for the select field.
 * @param required - If true, select is required.
 * @param label - Label for the select field.
 * @param error - If true, select gain error status.
 * @param defaultValue - Default value for the select field. Default empty string "".
 * @param value - Value for the select field. Used in case you need the select the be controlled.
 * @param size - Sets the style of the dropdown items. default 'large'.
 * @param searchable - If true, the dropdown will be searchable.
 * @param className - Additional class name for the dropdown wrapper
 * @constructor
 */
export const SelectDropdown = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      id,
      name,
      disabled,
      onChange,
      onFocus,
      onBlur,
      options = [],
      assistiveText,
      required,
      label,
      error,
      defaultValue = '',
      size = 'large',
      searchable = false,
      className,
      ...restProps
    },
    ref,
  ) => {
    const placeOptionValueToLabel = useCallback(
      (optionValue: string) => {
        const option = options.find(option => option.value === optionValue);
        return option?.label ?? '';
      },
      [options],
    );

    const [searchText, setSearchText] = useState<string>('');
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [value, setValue] = useState<string>(defaultValue);
    const [valueLabel, setValueLabel] = useState<string>(placeOptionValueToLabel(defaultValue));
    const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false); // Dropdown visibility state
    const [activeOptionIndex, setActiveOptionIndex] = useState<number>(-1);
    const [ignoreSpaceKeystrokes, setIgnoreSpaceKeystrokes] = useState<boolean>(false);
    const [isKeyboardNav, setIsKeyboardNav] = useState<boolean>(false);
    const selectDropdownContainerRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const selectRef = useRef<HTMLSelectElement | null>(null);
    const dropdownListRef = useRef<HTMLUListElement | null>(null);
    const assistiveTextId = assistiveText ? `${id}-text` : undefined;

    const selectedValueLabel = useMemo(() => {
      if (!options?.length) {
        return '';
      }

      if (!value) {
        return '';
      }

      return options.find(option => option.value === value)?.label;
    }, [value, options]);

    const visibleOptions = useMemo(() => {
      let filteredOptions = options;

      if (searchText && searchable) {
        filteredOptions = filteredOptions.filter(option =>
          option.label.toLowerCase().includes(searchText.toLowerCase()),
        );
      }

      return filteredOptions.map((option, index) => ({
        ...option,
        optionId: `${id}-option-${encodeURIComponent(option.value)}-${index}`,
      }));
    }, [options, searchText, searchable, id]);

    const numItems = visibleOptions.length;

    const initRefs = (element: HTMLSelectElement) => {
      selectRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    const focusHandler = (e: FocusEvent<HTMLDivElement>) => {
      setIsFocused(true);
      if (onFocus && !disabled) {
        onFocus(e);
      }
    };

    const blurHandler = (e: FocusEvent<HTMLDivElement>) => {
      setIsFocused(false);
      if (onBlur && !disabled) {
        onBlur(e);
      }
    };

    const changeHandler = (e: ChangeEvent<HTMLSelectElement>) => {
      if (onChange && !disabled) {
        onChange(e);
      }
    };

    const searchFocusHandler = () => {
      setIgnoreSpaceKeystrokes(true);
    };
    const searchBlurHandler = () => {
      setIgnoreSpaceKeystrokes(false);
    };

    const focusVisibleOption = useCallback(
      (targetIndex: number) => {
        if (!isDropdownVisible || numItems === 0) {
          return;
        }

        let normalizedIndex = targetIndex;
        if (targetIndex < 0) {
          normalizedIndex = numItems - 1;
        } else if (targetIndex >= numItems) {
          normalizedIndex = 0;
        }

        setActiveOptionIndex(normalizedIndex);

        const targetElement = dropdownListRef.current?.children?.[normalizedIndex] as
          | HTMLElement
          | undefined;
        targetElement?.scrollIntoView({ block: 'nearest' });
      },
      [isDropdownVisible, numItems],
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      setIsKeyboardNav(true);

      if (
        e.code === 'Enter' ||
        (searchText.length < 1 && e.code === 'Space') || // Product requirement: Allow space key to open dropdown when no search text is present
        (!ignoreSpaceKeystrokes && e.code === 'Space') ||
        (searchable && e.code === 'Enter')
      ) {
        e.preventDefault();
        if (isDropdownVisible) {
          if (activeOptionIndex >= 0 && visibleOptions[activeOptionIndex]) {
            handleSelect(visibleOptions[activeOptionIndex].value);
          } else {
            setIsDropdownVisible(false);
          }
          dropdownRef?.current?.focus();
        } else if (!disabled) {
          toggleDropdown();
        }
      }

      // If Tab is pressed while the dropdown is open, close it so focus can advance to the next
      // element in the page's tab order (default browser Tab behavior).
      if (e.key === 'Tab' && isDropdownVisible) {
        setIsDropdownVisible(false);
        setSearchText('');
      }

      if (e.key === 'ArrowDown' && isDropdownVisible) {
        e.preventDefault();
        if (numItems === 0) return;
        const nextIndex = activeOptionIndex < 0 ? 0 : activeOptionIndex + 1;
        focusVisibleOption(nextIndex);
      }

      if (e.key === 'ArrowUp' && isDropdownVisible) {
        e.preventDefault();
        if (numItems === 0) return;
        const previousIndex = activeOptionIndex < 0 ? numItems - 1 : activeOptionIndex - 1;
        focusVisibleOption(previousIndex);
      }

      if (e.key === 'Escape' && isDropdownVisible) {
        e.preventDefault();
        e.stopPropagation();
        setIsDropdownVisible(false);
        dropdownRef.current?.focus();
        setSearchText('');
      }

      // Home key - jump to first option
      if (e.key === 'Home' && isDropdownVisible) {
        if (!searchable) {
          e.preventDefault();
          if (numItems === 0) return;
          focusVisibleOption(0);
        }
      }

      // End key - jump to last option
      if (e.key === 'End' && isDropdownVisible) {
        if (!searchable) {
          e.preventDefault();
          if (numItems === 0) return;
          focusVisibleOption(numItems - 1);
        }
      }

      // Type-ahead search: single printable character jumps to matching option
      if (
        isDropdownVisible &&
        !searchable &&
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.metaKey
      ) {
        const char = e.key.toLowerCase();
        if (numItems > 0) {
          const startIndex = activeOptionIndex >= 0 ? activeOptionIndex + 1 : 0;
          let matchIndex = -1;

          for (let offset = 0; offset < numItems; offset += 1) {
            const candidateIndex = (startIndex + offset) % numItems;
            const optionLabel = visibleOptions[candidateIndex]?.label;
            if (optionLabel && optionLabel.toLowerCase().startsWith(char)) {
              matchIndex = candidateIndex;
              break;
            }
          }

          if (matchIndex !== -1) {
            focusVisibleOption(matchIndex);
          }
        }
      }
    };

    // Toggle the dropdown visibility
    const toggleDropdown = () => {
      if (disabled) {
        return;
      }

      if (!isDropdownVisible) {
        const currentIndex = visibleOptions.findIndex(option => option.value === value);
        setActiveOptionIndex(currentIndex >= 0 ? currentIndex : visibleOptions.length ? 0 : -1);
      }

      setIsDropdownVisible((prev: boolean) => !prev);
    };

    // Handle selection
    const handleSelect = useCallback((propValue: string) => {
      setValue(propValue);
      setSearchText('');
      setIsDropdownVisible(false);
      if (selectRef.current) {
        selectRef.current.value = propValue;
        const changeEvent = new Event('change', { bubbles: true });
        selectRef.current.dispatchEvent(changeEvent);
      }
    }, []);

    const dropdownItems = useMemo(
      () =>
        visibleOptions.map((option, index) => ({
          id: option.optionId,
          label: option.label,
          selected: option.value === value,
          isActive: isKeyboardNav && isDropdownVisible && index === activeOptionIndex,
          role: 'option' as const,
          topDivider: false,
          tabIndex: -1,
          onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
          onClick: () => {
            handleSelect(option.value);
            dropdownRef.current?.focus();
          },
          onMouseOver: () => {
            setIsKeyboardNav(false);
            focusVisibleOption(index);
          },
        })),
      [
        visibleOptions,
        isDropdownVisible,
        isKeyboardNav,
        activeOptionIndex,
        value,
        handleSelect,
        focusVisibleOption,
      ],
    );

    const handleClickOutside = useCallback(
      (event: MouseEvent) => {
        if (
          selectDropdownContainerRef.current &&
          !selectDropdownContainerRef.current.contains(event.target as Node)
        ) {
          setIsDropdownVisible(false);
          setSearchText('');
        }
      },
      [selectDropdownContainerRef],
    );

    // Close dropdown when clicking outside
    useEffect(() => {
      if (!isDropdownVisible) {
        setValueLabel(placeOptionValueToLabel(value));
        setActiveOptionIndex(-1);
      }

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [value, isDropdownVisible, handleClickOutside, placeOptionValueToLabel]);

    useEffect(() => {
      if (isDropdownVisible && activeOptionIndex >= 0 && !searchable) {
        const activeOption = visibleOptions[activeOptionIndex];
        if (activeOption) {
          setValueLabel(activeOption.label);
        }
      }
    }, [isDropdownVisible, activeOptionIndex, visibleOptions, searchable]);

    useEffect(() => {
      if (!isDropdownVisible) {
        return;
      }

      if (numItems === 0) {
        setActiveOptionIndex(-1);
        return;
      }

      if (activeOptionIndex >= numItems) {
        setActiveOptionIndex(numItems - 1);
      }
    }, [numItems, isDropdownVisible, activeOptionIndex]);

    // Compute active descendant ID for aria-activedescendant
    const activeDescendantId =
      isDropdownVisible && activeOptionIndex >= 0
        ? visibleOptions[activeOptionIndex]?.optionId
        : undefined;

    const comboboxProps = !searchable
      ? {
          role: 'combobox',
          'aria-haspopup': 'listbox' as const,
          'aria-expanded': isDropdownVisible,
          'aria-controls': `${id}-list`,
          'aria-labelledby': label ? `${id}-label ${id}-value` : undefined,
          'aria-activedescendant': activeDescendantId,
          'aria-describedby': assistiveTextId,
          'aria-disabled': disabled,
          'aria-required': required,
          'aria-invalid': error,
          tabIndex: disabled ? -1 : 0,
        }
      : {};

    return (
      <div
        ref={selectDropdownContainerRef}
        onKeyDown={handleKeyDown}
        data-open={isDropdownVisible || undefined}
        className={classNames('vds-select-dropdown', className, {
          'vds-select-dropdown--filled': valueLabel,
          'vds-select-dropdown--active': !searchable && (isFocused || isDropdownVisible),
          'vds-select-dropdown--error': !searchable && error,
          'vds-select-dropdown--disabled': !searchable && disabled,
        })}>
        <div
          ref={dropdownRef}
          className={classNames('vds-select-dropdown__control', {
            'vds-select-dropdown__regular': !searchable,
          })}
          onClick={e => {
            // A click on the control means this is a pointer interaction, not keyboard
            setIsKeyboardNav(false);
            toggleDropdown();
          }}
          onBlur={blurHandler}
          onFocus={focusHandler}
          {...comboboxProps}>
          {/* Label is associated with the combobox via aria-labelledby, not htmlFor.
             htmlFor would target the hidden native <select>, causing a synthetic click
             that bubbles and double-toggles the dropdown. */}
          {!searchable && label ? <label id={`${id}-label`}>{label}</label> : null}
          <select
            ref={initRefs}
            required={required}
            id={id}
            name={name}
            value={value}
            disabled={disabled}
            onChange={changeHandler}
            aria-describedby={assistiveTextId}
            {...restProps}
            tabIndex={-1}>
            {options.map((option, index) => (
              <option key={`${option.value}${index}`} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {searchable ? (
            <Input
              id={`search-for-${id}`}
              endIcon={isDropdownVisible ? 'chevron-upward' : 'chevron-downward'}
              label={label}
              onChange={e => setSearchText(e.target.value)}
              value={searchText}
              role="combobox"
              aria-autocomplete="list"
              aria-haspopup="listbox"
              aria-expanded={isDropdownVisible}
              aria-controls={`${id}-list`}
              aria-activedescendant={activeDescendantId}
              aria-describedby={assistiveTextId}
              placeholder={!value ? '' : selectedValueLabel}
              error={error}
              disabled={disabled}
              required={required}
              onFocus={searchFocusHandler}
              onBlur={searchBlurHandler}
              isPlaceholderColored
            />
          ) : (
            <>
              <div className="vds-select-dropdown__value" id={`${id}-value`}>
                {valueLabel}
              </div>
              <span className="vds-select-dropdown__icon">
                <Icon
                  name={isDropdownVisible ? 'chevron-upward' : 'chevron-downward'}
                  size="small"
                />
              </span>
            </>
          )}
        </div>
        <div
          className={classNames(
            'vds-select-dropdown__items',
            isDropdownVisible && 'vds-select-dropdown__items--open',
          )}>
          <DropdownList
            id={`${id}-list`}
            className="vds-select-dropdown__list"
            ref={dropdownListRef}
            size={size}
            items={dropdownItems}
            isOpen={isDropdownVisible}
            isProfileDropdown={false}
            role="listbox"
          />
        </div>
        {assistiveText ? (
          <AssistiveText
            className="vds-select-dropdown__assistive-text"
            error={error}
            disabled={disabled}
            id={assistiveTextId}>
            {assistiveText}
          </AssistiveText>
        ) : null}
      </div>
    );
  },
);
